import React, { type ReactNode, useEffect, useRef, useState } from 'react'
import { Divider, ListItem, Paragraph, ResponsiveTableRow, Row, Separator, Skeleton } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { getSessionId } from '@fs/zion-session'
import { Link } from '@fs/zion-router'
import { type GroupSummary, type TreeId } from '@fs/zion-tree-types'
import noGroupImg from '../upload-page/assets/no-group.jpg'
import ProcessingFileText from '../upload-page/upload-row/ProcessingFileText'
import getTreeUrl from '../../../helpers/getTreeUrl'
import { useTreeDetails } from '../upload-page/upload-row/useTreeDetails'
import { useShowLoadingBar } from '../upload-page/upload-row/useShowLoadingBar'
import CetUploadError from '../upload-page/upload-row/CetUploadError'
import { type GedcomFileFailed, type GedcomFile } from '../../../services/groupTypes'
import ManageTreeRowActions, { ManageTreeRowActionsSkeleton } from './ManageTreeRowActions'
import { formatDate } from '../../helpers'
import { useLastTreeChange } from '../../../helpers/useLastTreeChange'
import { useTreeRole } from '../../../helpers/useTreeRole'

/** The tree thumbnail in both layouts. The fixed width holds the slot open even when the image will not load. */
const thumbnailCss = css`
  border-radius: 8px;
  overflow: hidden;
  width: 79px;

  img {
    border-radius: 8px;
    max-width: 79px;
  }
`

const linkStyleCss = css`
  text-decoration: none;
`

/** `rowLevelHeaders` makes the name cell a `<th>`; undo the browser's default bold. */
const nameCellCss = css`
  font-weight: normal;
`

type TableRowNameProps = {
  children: ReactNode
  /** Called when `src` fails to load, so the caller can swap in the placeholder. */
  onImageError?: () => void
  src: string
}

function TableRowName({ children, onImageError, src }: TableRowNameProps): JSX.Element {
  return (
    <Row alignItemsY="middle" css={nameCellCss} gutters="xs">
      <div css={thumbnailCss} noShrinkX>
        <img src={src} alt="" onError={onImageError} />
      </div>
      {children}
    </Row>
  )
}

type GetTestIdParams = {
  isResearcherTree: boolean
  treeId?: TreeId | null
  groupId?: string
  fileName?: string
}

const getTestId = ({ isResearcherTree, treeId, groupId, fileName }: GetTestIdParams): string => {
  const treeType = isResearcherTree ? 'cet' : 'family-group'
  const listId = treeId || groupId || fileName
  const suffix = listId ? `:${listId}` : ''
  return `manage-tree-list:${treeType}${suffix}`
}

/** Name the per-cell placeholders a loaded row never shows, so a test can tell them from real values. */
const DATE_LOADING_TEST_ID = 'ManageTreeRowDateLoading'
const ROLE_LOADING_TEST_ID = 'ManageTreeRowRoleLoading'

type ManageTreeRowProps =
  | {
      asTableRow?: boolean
      group: GroupSummary
      file?: GedcomFile
      failedFile?: never
      processingFile?: never
      isFamilyGroupTree?: boolean
      isMobile: boolean
      isLast?: boolean
      showCombinedDesign?: boolean
      currentUserCisId?: string
    }
  | {
      asTableRow?: boolean
      group?: never
      file?: never
      failedFile?: never
      processingFile: GedcomFile
      isFamilyGroupTree?: boolean
      isMobile: boolean
      isLast?: boolean
      showCombinedDesign?: boolean
      currentUserCisId?: never
    }
  | {
      asTableRow?: boolean
      group?: never
      file?: never
      failedFile: GedcomFileFailed
      processingFile?: never
      isFamilyGroupTree?: boolean
      isMobile: boolean
      isLast?: boolean
      showCombinedDesign?: boolean
      currentUserCisId?: never
    }

function ManageTreeRow({
  asTableRow,
  group,
  file,
  failedFile,
  processingFile,
  isFamilyGroupTree,
  isMobile,
  isLast,
  showCombinedDesign,
  currentUserCisId,
}: ManageTreeRowProps): JSX.Element {
  const [t] = useTranslation()

  // The family group table drops its date column, so full-width rows span one fewer.
  const totalColumnCount = isFamilyGroupTree ? 3 : 4

  // Extract values for hooks (must be called before any conditional returns)
  const treeId = group?.treeIds?.[0]
  const isResearcherTree = group?.types?.includes('RESEARCHER_TREE') ?? false
  const fileStatus = file?.fileStatus ?? processingFile?.fileStatus
  const percentComplete = file?.percentComplete ?? processingFile?.percentComplete

  const sessionId = getSessionId()
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const { treeDetails, refetchTreeDetails, loading: loadingTreeDetails } = useTreeDetails(treeId)
  const [{ isImporting, isProcessing }] = useShowLoadingBar({ fileStatus, percentComplete })
  const prevFileStatusRef = useRef(fileStatus)
  // Only researcher trees have a change log, and only the combined design shows a date.
  const showsDate = isResearcherTree && Boolean(showCombinedDesign)
  const {
    failed: lastChangeFailed,
    lastChangeMs,
    loading: loadingLastChange,
    refetchLastChange,
  } = useLastTreeChange(showsDate ? treeId : undefined)

  // An empty change log means nothing has happened since the tree was created, so its creation date is when it last changed
  let modifiedDate = lastChangeMs ?? treeDetails?.createDate

  if (lastChangeFailed) {
    // A log that could not answer is not a tree with no changes, so the creation date would overstate it.
    modifiedDate = undefined
  }

  const formattedDate = modifiedDate ? formatDate(modifiedDate, 'MM/dd/yyyy') : undefined

  const roleLabel = useTreeRole({
    isResearcherTree,
    memberCisId: currentUserCisId,
    memberStatus: group?.memberStatus,
    ownerCisId: treeDetails?.ownerCisId,
  })

  // Refetch tree details when import completes to get updated privacy status. The change log is read
  // again for the same reason: the import is itself a change, and the row read the log before it landed.
  useEffect(() => {
    const prevStatus = prevFileStatusRef.current
    prevFileStatusRef.current = fileStatus

    if (prevStatus === 'IMPORTING' && fileStatus === 'IMPORTED' && treeId) {
      refetchTreeDetails()
      refetchLastChange()
    }
  }, [fileStatus, treeId, refetchLastChange, refetchTreeDetails])

  if (failedFile) {
    const imageSrc = noGroupImg
    const failedTestId = `manage-tree-list:failed-file:${failedFile.fileName}`
    const uploadError = <CetUploadError fileName={failedFile.fileName} fileStatus={failedFile.fileStatus} />

    if (asTableRow) {
      return (
        <>
          {/* A failed upload has no date, role, or tools to report, so only the name column is filled. */}
          <ResponsiveTableRow
            data-testid={failedTestId}
            noBottomDivider
            column1={<TableRowName src={imageSrc}>{failedFile.fileName}</TableRowName>}
          />
          {/* The alert spans the full row; keep this in step with the table's `totalColumnCount`. */}
          <ResponsiveTableRow column1span={totalColumnCount} column1={uploadError} />
        </>
      )
    }

    return (
      <>
        <ListItem
          data-testid={failedTestId}
          dense={isMobile}
          startElement={
            <div css={thumbnailCss}>
              <ListItem.Image size="md" src={imageSrc} />
            </div>
          }
          primaryText={failedFile.fileName}
        />
        {uploadError}
      </>
    )
  }

  if (processingFile) {
    const imageSrc = noGroupImg
    const processingFileIsProcessing = isProcessing || isImporting
    const processingTestId = `manage-tree-list:processing-file:${processingFile.fileName}`
    const processingName = (
      <>
        {processingFile.title}
        <ProcessingFileText />
      </>
    )
    // No `showCombinedDesign` or `asTableRow` here: a processing row has no group, so `CetActions`
    // renders nothing to stack, and its progress bar sits in a full-width row that wants the wider size.
    const processingActions = (
      <ManageTreeRowActions
        file={processingFile}
        group={undefined}
        isProcessingOrLoading={processingFileIsProcessing}
        isPrivateTree={undefined}
        isMobile={isMobile}
      />
    )

    const showProgressBar = processingFile.fileStatus === 'IMPORTING' && processingFile.percentComplete != null

    if (asTableRow) {
      // The progress bar is too wide for the Tools column, so it takes a full-width row instead.
      let toolsCell: JSX.Element | undefined
      if (!showProgressBar) toolsCell = processingActions

      return (
        <>
          <ResponsiveTableRow
            data-testid={processingTestId}
            noBottomDivider={showProgressBar}
            column1={<TableRowName src={imageSrc}>{processingName}</TableRowName>}
            column4={toolsCell}
          />
          {showProgressBar && <ResponsiveTableRow column1span={totalColumnCount} column1={processingActions} />}
        </>
      )
    }

    return (
      <>
        <ListItem
          data-testid={processingTestId}
          dense={isMobile}
          startElement={
            <div css={thumbnailCss}>
              <ListItem.Image size="md" src={imageSrc} />
            </div>
          }
          primaryText={processingName}
          endElement={!isMobile && processingActions}
        />
        {isMobile && (
          <>
            {showProgressBar && (
              <>
                <Separator size="pico" />
                {processingActions}
              </>
            )}

            {!isLast && <Separator size="sm" />}
          </>
        )}
      </>
    )
  }

  // group is guaranteed to be defined if failedFile and processingFile are not
  const name = group.name
  const groupId = group.id
  const imageUrl = group.imageUrl
  const fileName = file?.fileName

  const candidateSrc = imageUrl ? `${imageUrl}?sessionId=${sessionId}` : null
  const imageSrc = candidateSrc && candidateSrc !== failedSrc ? candidateSrc : noGroupImg
  const isProcessingOrLoading = isProcessing || isImporting
  const isPrivateTree = treeDetails?.private

  const treeUrl = getTreeUrl({
    treeId,
    mePersonId: treeDetails?.startingPersonId,
  })

  const testId = getTestId({ isResearcherTree, treeId, groupId, fileName })

  function getPrimaryText(): JSX.Element | string {
    if (isProcessingOrLoading && !treeId) {
      return (
        <>
          {name}
          <ProcessingFileText />
        </>
      )
    }
    // `isImporting` goes false at 100% so the progress bar can stop rendering, but the import is still
    // running then, and the tree is not openable until the status itself says IMPORTED.
    if (fileStatus === 'IMPORTING') {
      return name
    }
    return (
      <Link css={linkStyleCss} to={treeUrl} external wayfinding>
        {name}
      </Link>
    )
  }

  const rowActions = isMobile ? undefined : (
    <ManageTreeRowActions
      file={file}
      group={group}
      isProcessingOrLoading={isProcessingOrLoading}
      isPrivateTree={isPrivateTree}
      isMobile={isMobile}
      asTableRow={asTableRow}
      showCombinedDesign={showCombinedDesign}
    />
  )

  /*
   * Date and Role each come from their own per-row request, and those land at different times from each
   * other and from the rows around them. Standing a placeholder in the cell until its own request
   * settles keeps the value from appearing out of an empty cell after the table has otherwise loaded.
   */

  /*
   * Waits on the log even with `createDate` in hand: swapping a shown date for a real change reads worse
   * than a placeholder. A failed log waits on nothing, having already settled the cell as empty.
   */
  const waitingOnDate = loadingLastChange || (!formattedDate && !lastChangeFailed && loadingTreeDetails)

  let dateCell: ReactNode = formattedDate
  if (waitingOnDate) {
    dateCell = <Skeleton.Paragraph data-testid={DATE_LOADING_TEST_ID} lineCount={1} maxWidth={80} />
  }

  // An owner and a co-owner are both ADMIN, so `useTreeRole` withholds the label until `ownerCisId`
  // arrives. Rows that need no such lookup keep their label and never show this.
  let roleCell: ReactNode = roleLabel
  if (!roleLabel && loadingTreeDetails) {
    roleCell = <Skeleton.Paragraph data-testid={ROLE_LOADING_TEST_ID} lineCount={1} maxWidth={80} />
  }

  // A family group table has no date column, so role and tools each sit one column to the left.
  let column2: ReactNode = roleCell
  let column3: ReactNode = rowActions
  let column4: ReactNode

  if (!isFamilyGroupTree) {
    column2 = dateCell
    column3 = roleCell
    column4 = rowActions
  }

  return (
    <>
      {asTableRow ? (
        <ResponsiveTableRow
          data-testid={testId}
          column1={
            <TableRowName src={imageSrc} onImageError={() => setFailedSrc(candidateSrc)}>
              {getPrimaryText()}
            </TableRowName>
          }
          column2={column2}
          column3={column3}
          column4={column4}
        />
      ) : (
        <ListItem
          data-testid={testId}
          dense={isMobile}
          startElement={
            // TREEWEB-11486 - We specify role="none" to avoid surfacing images to screen readers that we have not captured functional alt text for.
            <div css={thumbnailCss}>
              <ListItem.Image role="none" size="md" src={imageSrc} onError={() => setFailedSrc(candidateSrc)} />
            </div>
          }
          primaryText={getPrimaryText()}
          endElement={
            isMobile ? undefined : (
              <ManageTreeRowActions
                file={file}
                group={group}
                isProcessingOrLoading={isProcessingOrLoading}
                isPrivateTree={isPrivateTree}
                isMobile={isMobile}
              />
            )
          }
        />
      )}
      {isMobile && (
        <>
          <Separator size="xs" />
          {showCombinedDesign && (
            <>
              {/* <Separator size="sm" /> */}
              {!isFamilyGroupTree && (
                <Paragraph size="sm">
                  {t('tree-import.manage-trees.table-date-heading')}: {dateCell}
                </Paragraph>
              )}
              <Paragraph size="sm">
                {t('tree-import.manage-trees.table-role-heading')}: {roleCell}
              </Paragraph>
              <Paragraph size="sm">{t('tree-import.manage-trees.table-tools-heading')}:</Paragraph>
            </>
          )}
          <ManageTreeRowActions
            file={file}
            group={group}
            isProcessingOrLoading={isProcessingOrLoading}
            isPrivateTree={isPrivateTree}
            isMobile={isMobile}
            showCombinedDesign={showCombinedDesign}
          />
          {showCombinedDesign && !isLast && (
            <>
              <Separator size="sm" />
              <Divider size="sm" />
            </>
          )}
          {!isLast && <Separator size="sm" />}
        </>
      )}
    </>
  )
}

export { ManageTreeRow }

/** Names the stand-in for a labelled Date, Role or Tools line, which only the mobile combined row has. */
const MOBILE_DETAIL_TEST_ID = 'ManageTreeRowSkeletonDetail'

type ManageTreeRowSkeletonProps = {
  asTableRow?: boolean
  isFamilyGroupTree?: boolean
  isLast?: boolean
  isMobile: boolean
  showCombinedDesign?: boolean
}

/**
 * Fills one row of whichever of the three shapes the loaded row will take: the desktop table row, the
 * combined design's taller mobile row, or the plain list row the pre-combined design still uses.
 */
export function ManageTreeRowSkeleton({
  asTableRow,
  isFamilyGroupTree,
  isLast,
  isMobile,
  showCombinedDesign,
}: ManageTreeRowSkeletonProps): JSX.Element {
  if (asTableRow) {
    // The name cell carries the thumbnail beside the name, the same way `TableRowName` does.
    const nameCell = (
      <Row alignItemsY="middle" gutters="xs">
        <Skeleton.Image width={79} height={56} />
        <Skeleton.TypeSkeleton maxWidth={250} randomSeed={3} />
      </Row>
    )

    // The Tools column stacks its buttons in the combined design, which only the table shape reaches.
    const toolsCell = <ManageTreeRowActionsSkeleton stacked />

    // Mirrors the loaded row: with no date column, a family group's role and tools each sit one to the left.
    let column2: ReactNode = <Skeleton.TypeSkeleton maxWidth={80} randomSeed={2} />
    let column3: ReactNode = toolsCell
    let column4: ReactNode

    if (!isFamilyGroupTree) {
      column2 = <Skeleton.TypeSkeleton maxWidth={80} randomSeed={1} />
      column3 = <Skeleton.TypeSkeleton maxWidth={80} randomSeed={2} />
      column4 = toolsCell
    }

    return (
      <ResponsiveTableRow
        aria-hidden
        data-testid="ManageTreeRowSkeleton"
        column1={nameCell}
        column2={column2}
        column3={column3}
        column4={column4}
      />
    )
  }

  const listItem = (
    <ListItem
      aria-hidden
      dense={isMobile}
      startElement={<Skeleton.Image width={79} height={56} />}
      primaryText={<Skeleton.TypeSkeleton maxWidth={250} randomSeed={3} />}
      endElement={isMobile ? undefined : <ManageTreeRowActionsSkeleton />}
      data-testid="ManageTreeRowSkeleton"
    />
  )

  // The combined design's mobile row carries more than the list row it replaces: a labelled Date, Role
  // and Tools line under the name, stacked action buttons, and a divider between rows.
  if (isMobile && showCombinedDesign) {
    return (
      <>
        {listItem}
        <Separator size="xs" />
        {!isFamilyGroupTree && (
          <Skeleton.Paragraph data-testid={MOBILE_DETAIL_TEST_ID} size="sm" lineCount={1} maxWidth={180} />
        )}
        <Skeleton.Paragraph data-testid={MOBILE_DETAIL_TEST_ID} size="sm" lineCount={1} maxWidth={140} />
        <Skeleton.Paragraph data-testid={MOBILE_DETAIL_TEST_ID} size="sm" lineCount={1} maxWidth={100} />
        <ManageTreeRowActionsSkeleton isMobile stacked />
        {!isLast && (
          <>
            <Separator size="sm" />
            <Divider size="sm" />
            <Separator size="sm" />
          </>
        )}
      </>
    )
  }

  return (
    <>
      {listItem}

      {isMobile && (
        <>
          <Separator size="pico" />
          <ManageTreeRowActionsSkeleton isMobile />
        </>
      )}
    </>
  )
}
